import { useState } from 'react';
import { getFromStorage, storeInLocal } from 'src/lib/storage';
import {
  ScheduleOutlined,
  SettingOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import {
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';
import { ArrowNarrowRight } from '@insight-health-ai/roseate/icons';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import { useSelector } from 'react-redux';

const apptRenamePopupTourSeenKey = 'appt_rename_popup_tour_seen';

export const useCareConnectSideMenus = () => {
  const [showApptRenamePopupTour, setShowApptRenamePopupTour] = useState(
    getFromStorage(apptRenamePopupTourSeenKey) !== 'true'
  );
  const { currentPractitionerSettings } = useSelector(
    (state) => state?.practitionerState
  );

  const { isEntitled: hasIntakeAccess } = useFeatureEntitlements('intake');
  const { isEntitled: hasFollowupAccess } = useFeatureEntitlements('followup');

  // Don't show appointments tab is both intake access and followup access are false
  if (!hasIntakeAccess && !hasFollowupAccess) {
    return [];
  }

  const isV2 = currentPractitionerSettings?.sidenav_preferences?.version === 2;

  const menus = [
    {
      path: '/appointments',
      title: isV2 ? 'Visits' : 'Appointments',
      icon: <ScheduleOutlined />,
      tourPopup: isV2
        ? {
            open: showApptRenamePopupTour,
            onOpenChange: () => {},
            title: (
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-2">
                  Appointments <ArrowNarrowRight /> Visits
                </span>
              </div>
            ),
            content:
              'Appointments is now renamed to Visits. You can still access your appointments by clicking the Visits tab.',
            primaryAction: {
              label: 'Okay',
              onClick: () => {
                setShowApptRenamePopupTour(false);
                storeInLocal(apptRenamePopupTourSeenKey, 'true');
              },
            },
          }
        : undefined,
    },
  ];

  return menus;
};

export const useSettingsSideMenus = () => {
  const { isSuperAdmin } = useGetCurrentPractitioner();

  const { isEntitled: isOPHqAccount } = useFeatureEntitlements('op-hq-account');
  const { isEntitled: isOPHqSuperAdmin } = usePermissionEntitlements(
    'op-hq-invite-practitioner'
  );
  const { isEntitled: isOpenDrHqAccount } =
    useFeatureEntitlements('open-dr-hq-account');
  const { isEntitled: isOpenDrHqSuperAdmin } = usePermissionEntitlements(
    'open-dr-hq-invite-practitioner'
  );
  const { isEntitled: isPhoneTriageModuleEntitled } =
    useFeatureEntitlements('phone-triage');
  const { isEntitled: hasCallReadAccess } =
    usePermissionEntitlements('calls.read');
  const { isEntitled: hasReferralsAccess } = useFeatureEntitlements('referral');

  const hasTagAccess =
    hasReferralsAccess ||
    isSuperAdmin ||
    (isPhoneTriageModuleEntitled && hasCallReadAccess);

  const settingsMenus = [
    {
      path: '/settings',
      title: 'Settings',
      icon: <SettingOutlined />,
      submenu: [
        ...(isOPHqAccount && isOPHqSuperAdmin
          ? [
              {
                path: '/op-settings',
                title: 'OP Admin',
                icon: (
                  <img
                    src="https://images.insighthealth.ai/office-practicum/logo.svg"
                    alt="Office Practicum Logo"
                    width={20}
                    height={20}
                    className="mr-[-4px]"
                  />
                ),
              },
            ]
          : []),
        ...(isOpenDrHqAccount && isOpenDrHqSuperAdmin
          ? [
              {
                path: '/open-dr-settings',
                title: 'OpenDr Admin',
                icon: <SettingOutlined />,
              },
            ]
          : []),
      ],
    },
  ];

  if (hasTagAccess) {
    settingsMenus[0].submenu.push({
      path: '/settings/tags',
      title: 'Tags',
      icon: <TagsOutlined />,
    });
  }

  return settingsMenus;
};
